// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Spin,
  Tag
} from "antd";
import {
  BookOutlined,
  CheckOutlined,
  CopyOutlined,
  LoadingOutlined,
  TeamOutlined
} from "@ant-design/icons";
import {
  KeywordInfoResponseDTO,
  NewsInfoResponseDTO,
  NewsThemeInfoResponseDTO,
  NewsWriteRequestDTO,
  SettingsInfoResponseDTO
} from "../../../../api/backend-openapi";
import {
  Block,
  Field,
  Image,
  NewsEditFormProps,
  NewsWorkflowStatus,
  PreviewFields
} from "../../../types/NewsTypes";
import {
  requestArchiveNews,
  requestDeleteNews,
  requestDestroyNews,
  requestDuplicateNews,
  requestGetNews,
  requestUnarchiveNews,
  requestUpdateNews,
  requestWorkflowNews,
  requestWorkflowPublishNews,
  requestWorkflowUnpublishNews
} from "../../../../state/news/NewsEffects";
import NewsSettingsFormSection from "./NewsSettingsFormSection";
import NewsContentInputFormSection from "./NewsContentInputFormSection";
import { NewsBlocks } from "../block/NewsBlocks";
import { getBase64UrlFrom } from "../../../../utils/ImageUtils";
import NewsImageForm from "./NewsImageForm";
import { formItemBlockLayout } from "../../../../utils/Constant";
import { navigate } from "@reach/router";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  SendOutlined,
  StopOutlined
} from "@ant-design/icons/lib";
import { validate } from "../../../../validator/NewsValidator";
import { deleteNews, updateNews } from "../../../../state/news/NewsEvents";
import DateFormat from "../../../../utils/DateFormat";
import ModalButton from "../../../components/ModalButton";
import i18n from "i18next";
import { showConfirm } from "../../../components/ConfirmModal";
import { requestGetSettings } from "../../../../state/settings/SettingsEffects";
import { NewsRefusedPublicationButton } from "./NewsRefusedPublicationButton";
import NewsStatusTag from "./NewsStatusTag";
import NewsPreviewButton from "./preview/NewsPreviewButton";
import NewsEditionPreviewButton from "./preview/NewsEditionPreviewButton";
import { ValidatorResult } from "../../../types/Types";
import { canBeDeleted, getStatusFrom } from "../../../../utils/NewsUtils";
import moment from "moment";
import { useStore } from "effector-react";
import { NewsThemeStore } from "../../../../state/newsTheme/NewsThemeStore";
import { requestGetNewsThemes } from "../../../../state/newsTheme/NewsThemeEffects";
import { displayError } from "../../../../utils/displayError";
import { KeywordStore } from "../../../../state/keyword/KeywordStore";

const { confirm } = Modal;

export interface NewsWorkflowStatusLayout {
  label: string;
  color: string;
}

const NewsForm: React.FunctionComponent<NewsEditFormProps> = (
  props: NewsEditFormProps
) => {
  const {
    newsId,
    editMode,
    setEditMode,
    canEdit,
    canDelete,
    canValidate
  } = props;

  const { t } = useTranslation();

  const [form] = Form.useForm();

  const [newsDetails, setNewsDetails] = useState<NewsInfoResponseDTO | null>(
    null
  );

  const newsThemeStore: NewsThemeInfoResponseDTO[] | null = useStore(
    NewsThemeStore
  );

  const [getResponseReceived, setGetResponseReceived] = useState<boolean>(
    false
  );

  const [previewFields, setPreviewFields] = useState<PreviewFields>({});

  const [blocks, setBlocks] = useState<Block[]>([]);

  const [fields, setFields] = useState<Field[]>([]);

  const [settings, setSettings] = useState<SettingsInfoResponseDTO | null>(
    null
  );

  const [branch, setBranch] = useState<string | undefined>(undefined);

  const [sendPushValueToDisplay, setSendPushValueToDisplay] = useState<boolean>(
    false
  );

  const [
    hideFromHomepageValueToDisplay,
    setHideFromHomepageValueToDisplay
  ] = useState(false);

  useEffect(() => {
    setBranch(
      newsThemeStore.find(
        newsTheme => newsTheme.id === newsDetails?.newsThemeId
      )?.branch || undefined
    );
    setSendPushValueToDisplay(newsDetails?.sendPush || false);
    setHideFromHomepageValueToDisplay(newsDetails?.hideFromHomepage || false);
  }, [newsThemeStore, newsDetails]);

  const computeCanSendPush = (news?: NewsInfoResponseDTO | null): boolean => {
    return (
      /*news?.priority === 3 && */ news?.scheduledAvailabilityDate === null
    );
  };

  const [canSendPush, setCanSendPush] = useState(
    computeCanSendPush(newsDetails)
  );

  const [priorityOkForPush, setPriorityOkForPush] = useState(
    /*newsDetails?.priority ? newsDetails.priority === 3 :*/ true
  );
  const [
    scheduledAvailabilityOkForPush,
    setScheduledAvailabilityOkForPush
  ] = useState(!newsDetails?.scheduledAvailabilityDate);

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const [newMainImage, setNewMainImage] = useState<Image | undefined>(
    undefined
  );

  const keywordsStore: KeywordInfoResponseDTO[] | null = useStore(KeywordStore);

  // Used as keys for react
  const generateBlockIds = (news: NewsInfoResponseDTO) => {
    const newsBlocks = JSON.parse(news.content);
    newsBlocks.forEach(
      (block: Block) => (block.idTempLocal = require("shortid").generate())
    );
    setBlocks(newsBlocks);
  };

  const resetFormFields = (data: NewsInfoResponseDTO) => {
    generateBlockIds(data);
    const newsTheme = newsThemeStore.find(
      (newsTheme: NewsThemeInfoResponseDTO) =>
        newsTheme.id === data?.newsThemeId
    );
    setFields([
      {
        name: ["id"],
        value: data.id
      },
      {
        name: ["creationDate"],
        value: DateFormat.standardDate(data.creationDate)
      },
      {
        name: ["modificationDate"],
        value: DateFormat.standardDate(data.modificationDate)
      },
      {
        name: ["eventDates"],
        value: [
          data.eventStartDate ? moment(data.eventStartDate) : undefined,
          data.eventEndDate ? moment(data.eventEndDate) : undefined
        ]
      },
      {
        name: ["title"],
        value: data.title
      },
      {
        name: ["priority"],
        value: data.priority.toString()
      },
      {
        name: ["audience"],
        value: data.audience
      },
      {
        name: ["broadcastListId"],
        value: data?.broadcastListId || ""
      },
      {
        name: ["scheduledAvailabilityDate"],
        value: data?.scheduledAvailabilityDate
          ? moment(data.scheduledAvailabilityDate)
          : ""
      },
      {
        name: ["scheduledUnpublishDate"],
        value: data?.scheduledUnpublishDate
          ? moment(data.scheduledUnpublishDate)
          : ""
      },
      {
        name: ["summary"],
        value: data.summary
      },
      {
        name: ["validatorReadingNote"],
        value: data.validatorReadingNote
      },
      ...(data.keywordIds
        ? [
            {
              name: ["keywords"],
              value: keywordsStore
                .filter(keyword => (data.keywordIds || []).includes(keyword.id))
                .map(keyword => ({
                  label: keyword.text,
                  value: keyword.id
                }))
            }
          ]
        : []),
      ...(data.newsThemeId
        ? [
            {
              name: ["newsThemeId"],
              value: data.newsThemeId
            }
          ]
        : []),
      ...(newsTheme
        ? [
            {
              name: ["branch"],
              value: newsTheme.branch
            }
          ]
        : [])
    ]);
    setPreviewFields({
      title: data.title,
      summary: data.summary,
      newsThemeId: data.newsThemeId
    });
    setBranch(newsTheme?.branch);
    setSendPushValueToDisplay(data.sendPush || false);
    setCanSendPush(computeCanSendPush(data));
    setPriorityOkForPush(/*data?.priority ? data.priority === 3 : */ true);
    setScheduledAvailabilityOkForPush(!data?.scheduledAvailabilityDate);
  };

  useEffect(() => {
    if (newsId) {
      requestGetNewsThemes();
    }
  }, [newsId]);

  useEffect(() => {
    return requestGetNewsThemes.done.watch(({ result }) => {
      if (result.ok && result.data) {
        requestGetNews(newsId);
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestGetNews.done.watch(({ result }) => {
      setGetResponseReceived(true);
      if (result.ok) {
        if (result.data) {
          setNewsDetails(result.data);
          resetFormFields(result.data);
        } else {
          displayError(result.errorMessage || "Error!", false);
        }
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestUpdateNews.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setEditMode(false);
        updateNews(result.data);
        setNewsDetails(result.data);
        message.success(t("edit_news.success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestDuplicateNews.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        navigate(`/news/${result.data.id}`);
        message.success(t("edit_news.duplicate_success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestWorkflowNews.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setEditMode(false);
        updateNews(result.data);
        setNewsDetails(result.data);
        message.success(t("edit_news.success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestWorkflowPublishNews.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setEditMode(false);
        updateNews(result.data);
        setNewsDetails(result.data);
        message.success(t("edit_news.publish_success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestWorkflowUnpublishNews.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setEditMode(false);
        updateNews(result.data);
        setNewsDetails(result.data);
        message.success(t("edit_news.unpublish_success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestArchiveNews.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setEditMode(false);
        updateNews(result.data);
        setNewsDetails(result.data);
        message.success(t("edit_news.archive_success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestUnarchiveNews.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setEditMode(false);
        updateNews(result.data);
        setNewsDetails(result.data);
        message.success(t("edit_news.unarchive_success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    requestGetSettings({});
  }, []);

  useEffect(() => {
    return requestGetSettings.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setSettings(result.data);
      }
    });
  });

  useEffect(() => {
    return requestDeleteNews.done.watch(({ params, result }) => {
      if (result.ok) {
        deleteNews(params.id);
        message.success(t("news.global_buttons.delete.success"));
        navigate("/news");
      } else {
        displayError("news.global_buttons.delete.error");
      }
    });
  });

  useEffect(() => {
    return requestDestroyNews.done.watch(({ params, result }) => {
      if (result.ok) {
        deleteNews(params.id);
        message.success(t("news.global_buttons.delete.success"));
        navigate("/news");
      } else {
        displayError("news.global_buttons.delete.error");
      }
    });
  });

  const spinIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const confirmationPopup = async () => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("edit_news.modal.title"),
        content: t("edit_news.modal.description"),
        okText: t("edit_news.modal.yes"),
        cancelText: t("edit_news.modal.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  const setValuesAndRequestUpdateNews = (
    values: any,
    status: NewsWorkflowStatus,
    mainImageData: string,
    mainImageFilename: string,
    mainImageType: string
  ) => {
    console.log("values", values);
    const newsToUpate: NewsWriteRequestDTO = {
      title: values.title,
      priority: values.priority,
      scheduledAvailabilityDate: values.scheduledAvailabilityDate,
      scheduledUnpublishDate: values.scheduledUnpublishDate,
      summary: values.summary,
      newsThemeId: values.newsThemeId,
      // Si la valeur dans le formulaire est un object avec label+value, on ne prend que la value, sinon on a déjà l'ID directement
      keywordIds: values.keywords
        ? values.keywords.map(
            (keyword: { value: string }) => keyword.value || keyword
          )
        : [],
      mainImageData: mainImageData,
      mainImageFilename: mainImageFilename,
      mainImageType: mainImageType,
      content: JSON.stringify(blocks),
      sendPush: sendPushValueToDisplay,
      eventStartDate: values.eventDates?.[0],
      eventEndDate: values.eventDates?.[1],
      status,
      audience: values.audience,
      broadcastListId: values.broadcastListId,
      hideFromHomepage: values.hideFromHomepage
    };
    requestUpdateNews({
      id: newsId,
      dto: newsToUpate
    });
  };

  const handleSubmit = (values: any, status: NewsWorkflowStatus) => {
    const validationResult: ValidatorResult = validate(blocks);
    if (!validationResult.validate) {
      displayError(validationResult.message || "Error!", false);
    } else {
      confirmationPopup().then((confirmed: boolean) => {
        if (confirmed) {
          newMainImage
            ? setValuesAndRequestUpdateNews(
                values,
                status,
                newMainImage.base64Data,
                newMainImage.name,
                newMainImage.type
              )
            : setValuesAndRequestUpdateNews(
                values,
                status,
                newsDetails ? newsDetails.mainImageData : "",
                newsDetails ? newsDetails.mainImageFilename : "",
                newsDetails ? newsDetails.mainImageType : ""
              );
          setButtonLoading(true);
        }
      });
    }
  };

  const handleSave = (values: any) => {
    handleSubmit(values, NewsWorkflowStatus.SAVED);
  };

  const handleSubmitToValidation = () => {
    form
      .validateFields()
      .then(values => {
        handleSubmit(values, NewsWorkflowStatus.VALIDATION);
      })
      .catch(() => {
        displayError("global.on_finish_failed");
      });
  };

  const onFinishFailed = ({ errorFields }: any) => {
    displayError("global.on_finish_failed");
    form.scrollToField(errorFields[0].name);
  };

  return (
    <>
      {!getResponseReceived ? (
        <div style={{ textAlign: "center" }}>
          <Spin indicator={spinIcon} />
        </div>
      ) : newsDetails ? (
        <Form
          form={form}
          {...formItemBlockLayout}
          onFinish={handleSave}
          onFinishFailed={onFinishFailed}
          fields={fields}
        >
          <div className="form-first-item-sticky-top-container">
            <div className="form-first-item-sticky-top-content">
              <div className="my-3">
                <NewsStatusTag status={getStatusFrom(newsDetails)} />
                {newsDetails.archived && (
                  <Tag color={"green"}>{t("news.archived")}</Tag>
                )}
              </div>

              <Divider orientation="left">
                <h3 className="text-secondary mb-0">
                  {t("news.news_actions")}
                </h3>
              </Divider>
              <div className="d-flex align-items-center justify-content-between flex-wrap px-3">
                <div className="my-3">
                  <div className="d-flex align-items-center justify-content-center flex-wrap">
                    {!editMode && (
                      <Button
                        htmlType="reset"
                        className="m-2"
                        onClick={() => navigate("/news")}
                      >
                        <ArrowLeftOutlined /> {t("edit_news.return")}
                      </Button>
                    )}
                    {editMode ? (
                      <NewsEditionPreviewButton
                        newsTitle={previewFields.title}
                        newsSummary={previewFields.summary}
                        newsImage={
                          newMainImage
                            ? getBase64UrlFrom(
                                newMainImage.base64Data,
                                newMainImage.type
                              )
                            : getBase64UrlFrom(
                                newsDetails.mainImageData,
                                newsDetails.mainImageType
                              )
                        }
                        blocks={blocks}
                        iconButton={false}
                        newsThemeId={previewFields.newsThemeId}
                        newsEventStartDate={
                          form.getFieldValue("eventDates")?.[0]
                        }
                        newsEventEndDate={form.getFieldValue("eventDates")?.[1]}
                      />
                    ) : (
                      <NewsPreviewButton
                        newsId={newsId}
                        iconButton={false}
                        newsThemeId={newsDetails.newsThemeId}
                      />
                    )}
                    <ModalButton
                      modalTitle={i18n.t("news_list.table.deep_link")}
                      modalBody={newsDetails.deepLink}
                      icon={<LinkOutlined />}
                      tooltipTitle={i18n.t("news_list.table.tooltip.deep_link")}
                      buttonClassName="btn-tertiary m-2"
                      iconButton={false}
                      footer={[
                        <Button
                          key="copy"
                          className="btn-primary"
                          onClick={() => {
                            navigator.clipboard.writeText(newsDetails.deepLink);
                            message.success(
                              i18n.t("edit_news.deeplink_copied")
                            );
                          }}
                        >
                          <CopyOutlined /> {t("edit_news.copy")}
                        </Button>
                      ]}
                    >
                      {t("edit_news.deeplink")}
                    </ModalButton>
                  </div>
                </div>
                <div>
                  {editMode ? (
                    <>
                      <div className="text-center m-2">
                        <Popconfirm
                          title={t("global.cancel_confirmation")}
                          okText={t("global.yes")}
                          cancelText={t("global.no")}
                          onConfirm={() => {
                            setEditMode(false);
                            setBlocks(JSON.parse(newsDetails.content));
                            resetFormFields(newsDetails);
                          }}
                          placement="top"
                          icon={<QuestionCircleOutlined />}
                          className="m-2"
                        >
                          <Button htmlType="reset">
                            <StopOutlined /> {t("edit_news.cancel")}
                          </Button>
                        </Popconfirm>
                        <Button
                          className="btn-primary m-2"
                          htmlType="submit"
                          loading={buttonLoading}
                        >
                          <SaveOutlined /> {t("edit_news.save")}
                        </Button>
                        <Button
                          className="btn-success m-2"
                          onClick={handleSubmitToValidation}
                          loading={buttonLoading}
                        >
                          <TeamOutlined /> {t("edit_news.validation")}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="d-flex align-items-center justify-content-center flex-wrap">
                        {canEdit && (
                          <Button
                            htmlType="reset"
                            className="btn-primary m-2"
                            onClick={() => {
                              setEditMode(true);
                              setSendPushValueToDisplay(
                                newsDetails.publicationDate
                                  ? newsDetails.latestPushDate
                                    ? true
                                    : false
                                  : newsDetails.sendPush
                              );
                            }}
                          >
                            <EditOutlined /> {t("edit_news.button")}
                          </Button>
                        )}
                        {newsDetails.status !== NewsWorkflowStatus.PUBLISHED &&
                          canEdit && (
                            <Button
                              className="btn-primary m-2"
                              onClick={() =>
                                showConfirm(
                                  newsDetails?.archived
                                    ? i18n.t("edit_news.modal.unarchive", {
                                        news: newsDetails.title
                                      })
                                    : i18n.t("edit_news.modal.archive", {
                                        news: newsDetails.title
                                      }),
                                  () => {
                                    newsDetails?.archived
                                      ? requestUnarchiveNews({
                                          id: newsDetails.id
                                        })
                                      : requestArchiveNews({
                                          id: newsDetails.id
                                        });
                                  }
                                )
                              }
                            >
                              <BookOutlined />{" "}
                              {newsDetails.archived
                                ? t("edit_news.unarchive")
                                : t("edit_news.archive")}
                            </Button>
                          )}
                        {newsDetails.status === NewsWorkflowStatus.SAVED &&
                          canEdit && (
                            <Button
                              className="btn-success m-2"
                              onClick={() =>
                                showConfirm(
                                  i18n.t(
                                    "news_list.table.confirmation.validation",
                                    {
                                      news: newsDetails.title
                                    }
                                  ),
                                  () => {
                                    requestWorkflowNews({
                                      id: newsDetails.id,
                                      dto: {
                                        status: NewsWorkflowStatus.VALIDATION
                                      }
                                    });
                                  }
                                )
                              }
                            >
                              <TeamOutlined /> {t("edit_news.validation")}
                            </Button>
                          )}
                        {newsDetails.status === NewsWorkflowStatus.VALIDATION &&
                          canValidate && (
                            <>
                              <NewsRefusedPublicationButton
                                newsId={newsDetails.id}
                              />
                              <Button
                                className="btn-success m-2"
                                onClick={() =>
                                  showConfirm(
                                    i18n.t(
                                      "news_list.table.confirmation.validate",
                                      {
                                        news: newsDetails.title
                                      }
                                    ),
                                    () => {
                                      requestWorkflowNews({
                                        id: newsDetails.id,
                                        dto: {
                                          status: NewsWorkflowStatus.VALIDATED
                                        }
                                      });
                                    }
                                  )
                                }
                              >
                                <CheckOutlined /> {t("edit_news.validate")}
                              </Button>
                            </>
                          )}
                        {newsDetails.status === NewsWorkflowStatus.VALIDATED &&
                          canValidate && (
                            <Button
                              className="btn-success m-2"
                              onClick={() =>
                                showConfirm(
                                  i18n.t(
                                    "news_list.table.confirmation.publish",
                                    {
                                      news: newsDetails.title
                                    }
                                  ),
                                  (reason?: string) => {
                                    requestWorkflowPublishNews({
                                      id: newsDetails.id,
                                      dto: {
                                        isPublish: true,
                                        publishNotes: reason
                                      }
                                    }).then(r => console.log("response", r));
                                  },
                                  undefined,
                                  newsDetails.isPublishedAtLeastOneTime
                                    ? (valueToTranslate: string) =>
                                        t(valueToTranslate)
                                    : undefined,
                                  newsDetails.isPublishedAtLeastOneTime
                                    ? "publish"
                                    : undefined
                                )
                              }
                            >
                              <SendOutlined /> {t("edit_news.publish")}
                            </Button>
                          )}
                        {newsDetails.status === NewsWorkflowStatus.PUBLISHED &&
                          canValidate && (
                            <Button
                              className="btn-secondary m-2"
                              onClick={() =>
                                showConfirm(
                                  i18n.t(
                                    "news_list.table.confirmation.unpublish",
                                    {
                                      news: newsDetails.title
                                    }
                                  ),
                                  (reason?: string) => {
                                    requestWorkflowUnpublishNews({
                                      id: newsDetails.id,
                                      dto: {
                                        isPublish: false,
                                        publishNotes: reason
                                      }
                                    }).then(r => console.log("response", r));
                                  },
                                  undefined,
                                  (valueToTranslate: string) =>
                                    t(valueToTranslate),
                                  "unpublish"
                                )
                              }
                            >
                              <StopOutlined /> {t("edit_news.unpublish")}
                            </Button>
                          )}
                        <Button
                          className="btn-primary m-2"
                          onClick={() =>
                            showConfirm(
                              i18n.t("news_list.table.confirmation.duplicate", {
                                news: newsDetails.title
                              }),
                              () => {
                                requestDuplicateNews(newsDetails);
                              }
                            )
                          }
                        >
                          <CopyOutlined /> {t("edit_news.duplicate")}
                        </Button>
                        {canBeDeleted(newsDetails) && canDelete && (
                          <Button
                            className="btn-danger m-2"
                            onClick={() =>
                              showConfirm(
                                i18n.t("news_list.table.confirmation.delete", {
                                  news: newsDetails.title
                                }),
                                () => {
                                  requestDeleteNews(newsDetails);
                                }
                              )
                            }
                          >
                            <DeleteOutlined /> {t("edit_news.delete")}
                          </Button>
                        )}
                        {!canBeDeleted(newsDetails) && canDelete && (
                          <Button
                            className="btn-danger m-2"
                            onClick={() =>
                              showConfirm(
                                i18n.t("news_list.table.confirmation.delete", {
                                  news: newsDetails.title
                                }),
                                () => {
                                  requestDestroyNews(newsDetails);
                                }
                              )
                            }
                          >
                            <DeleteOutlined /> {t("edit_news.delete")}
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {newsDetails.status === NewsWorkflowStatus.REFUSED && (
                <Row>
                  <Col xs={24}>
                    <Form.Item
                      name="validatorReadingNote"
                      label={
                        <span>
                          {t("edit_news.validator_reading_note.text")}
                        </span>
                      }
                    >
                      <Input.TextArea
                        id="validatorReadingNote"
                        readOnly
                        className="readonly-textarea"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </div>
          </div>
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">{t("news.settings")}</h3>
          </Divider>
          <NewsSettingsFormSection
            news={newsDetails}
            editMode={editMode}
            previewFields={previewFields}
            setPreviewFields={setPreviewFields}
            branch={branch}
            setBranch={setBranch}
            canSendPush={canSendPush}
            setCanSendPush={setCanSendPush}
            sendPushValueToDisplay={sendPushValueToDisplay}
            setSendPushValueToDisplay={setSendPushValueToDisplay}
            hideFromHomepageValueToDisplay={hideFromHomepageValueToDisplay}
            setHideFromHomepageValueToDisplay={
              setHideFromHomepageValueToDisplay
            }
            priorityOkForPush={priorityOkForPush}
            setPriorityOkForPush={setPriorityOkForPush}
            scheduledAvailabilityOkForPush={scheduledAvailabilityOkForPush}
            setScheduledAvailabilityOkForPush={
              setScheduledAvailabilityOkForPush
            }
            resetNewsTheme={() =>
              form.setFields([
                {
                  name: "newsThemeId",
                  value: null
                }
              ])
            }
            showRequired={editMode}
          />
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">{t("news.news_content")}</h3>
          </Divider>
          <NewsContentInputFormSection
            news={newsDetails}
            showRequired={editMode}
            editMode={editMode}
            previewFields={previewFields}
            setPreviewFields={setPreviewFields}
          />
          <NewsImageForm
            formFieldName="mainImage"
            formFieldLabel={t("add_news.main_image")}
            base64Url={getBase64UrlFrom(
              newsDetails.mainImageData,
              newsDetails.mainImageType
            )}
            editMode={editMode}
            rules={{
              width: settings?.newsMainImageWidth,
              height: settings?.newsMainImageHeight,
              size: settings?.newsMainImageSize
            }}
            setImage={setNewMainImage}
          />
          {(blocks.length || editMode) && (
            <>
              <NewsBlocks
                editMode={editMode}
                blocks={blocks}
                setBlocks={setBlocks}
              />
            </>
          )}
        </Form>
      ) : (
        <div>{t("news.not_found")}</div>
      )}
    </>
  );
};

export default NewsForm;
